import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { 
    Users, UserPlus, X, CheckCircle2, AlertTriangle, ChevronDown, ChevronRight, ShieldCheck, UserCheck 
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../../components/Sidebar';
import { getUserPermissions } from '../../utils/permissions_utils';
import API, { socket } from '../../services/api';

const Eleves = ({ user }) => {
    const navigate = useNavigate();
    const userPerms = useMemo(() => getUserPermissions(), []);
    
    const [activeTab, setActiveTab] = useState('liste'); // 'liste' ou 'tuteurs'
    const [loading, setLoading] = useState(false);
    const [actionLoading, setActionLoading] = useState(false);
    const [toast, setToast] = useState({ show: false, message: '', type: 'success' });
    const [showForm, setShowForm] = useState(false);
    
    const [eleves, setEleves] = useState([]);
    const [tuteursListe, setTuteursListe] = useState([]);
    const [classes, setClasses] = useState([]);
    const [annees, setAnnees] = useState([]);
    const [civilites, setCivilites] = useState([]);
    
    // Accordéons
    const [elevesOuverts, setElevesOuverts] = useState({});
    const [tuteursOuverts, setTuteursOuverts] = useState({});

    // Formulaire d'ajout d'élève / tuteur
    const [eleveForm, setEleveForm] = useState({
        nom_eleve: '',
        telephone_eleve: '',
        email_eleve: '',
        adresse_eleve: '',
        civilite_id: '',
        classe_id: '',
        annee_scolaire_id: '',
        nom_tuteur: '',
        telephone_tuteur: '',
        email_tuteur: '',
        adresse_tuteur: '',
        lien_parente: 'Parent',
        creer_compte_tuteur: false
    });

    const showToast = (message, type = 'success') => {
        setToast({ show: true, message, type });
        setTimeout(() => setToast({ show: false, message: '', type: 'success' }), 5000);
    };

    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            const [resEleves, resClasses, resAnnees, resCivilites, resTuteurs] = await Promise.all([
                API.get('/scolarite/eleves/liste').catch(() => ({ data: { success: false, data: [] } })),
                API.get('/scolarite/classes/liste').catch(() => ({ data: { success: false, data: [] } })),
                API.get('/scolarite/annees/liste').catch(() => ({ data: { success: false, data: [] } })),
                API.get('/scolarite/civilites/liste').catch(() => ({ data: { success: false, data: [] } })),
                API.get('/scolarite/tuteurs/liste').catch(() => ({ data: { success: false, data: [] } }))
            ]);

            if (resEleves.data.success) setEleves(resEleves.data.data);
            if (resClasses.data.success) setClasses(resClasses.data.data);
            if (resAnnees.data.success) {
                const listeAnnees = resAnnees.data.data;
                setAnnees(listeAnnees);
                const ouverte = listeAnnees.find(a => a.statut === 'OUVERT') || listeAnnees[0];
                if (ouverte && !eleveForm.annee_scolaire_id) {
                    setEleveForm(prev => ({ ...prev, annee_scolaire_id: ouverte.id }));
                }
            }
            if (resCivilites.data.success) setCivilites(resCivilites.data.data);
            if (resTuteurs.data.success) setTuteursListe(resTuteurs.data.data);

        } catch (err) {
            showToast("Erreur lors du chargement des données", "error");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchData();
        if (socket && user?.company_id) {
            const room = String(user.company_id);
            socket.emit('join_company', room);
            socket.on('DATA_EVENT', (event) => {
                if (['customers', 'inscriptions', 'eleves_tuteurs', 'users'].includes(event.table)) {
                    fetchData();
                }
            });
        }
        return () => {
            if (socket) socket.off('DATA_EVENT');
        };
    }, [fetchData, user?.company_id]);

    const resetForm = () => {
        setShowForm(false);
        setEleveForm({
            nom_eleve: '',
            telephone_eleve: '',
            email_eleve: '',
            adresse_eleve: '',
            civilite_id: '',
            classe_id: '',
            annee_scolaire_id: annees.find(a => a.statut === 'OUVERT')?.id || '',
            nom_tuteur: '',
            telephone_tuteur: '',
            email_tuteur: '',
            adresse_tuteur: '',
            lien_parente: 'Parent',
            creer_compte_tuteur: false
        });
    };

    const toggleEleveAccordion = (id) => {
        setElevesOuverts(prev => ({ ...prev, [id]: !prev[id] }));
    };

    const toggleTuteurAccordion = (id) => {
        setTuteursOuverts(prev => ({ ...prev, [id]: !prev[id] }));
    };

    const handleCreateEleve = async (e) => {
        e.preventDefault();
        setActionLoading(true);
        try {
            await API.post('/scolarite/eleves/creer', eleveForm);
            showToast("Élève et inscription enregistrés avec succès.");
            resetForm();
            fetchData();
        } catch (err) {
            showToast(err.response?.data?.error || "Erreur lors de la création de l'élève", "error");
        } finally {
            setActionLoading(false);
        }
    };

    return (
        <div style={layoutStyle}>
            <Sidebar />
            <main style={mainStyle}>
                <header style={headerStyle}>
                    <div style={titleContainer}>
                        <div style={iconBoxStyle}><Users size={22} color="white" /></div>
                        <div>
                            <h1 style={titleStyle}>GESTION DES ÉLÈVES & TUTEURS</h1>
                            <div style={subtitleStyle}><ShieldCheck size={14} /> Inscriptions et Liens Familiaux</div>
                        </div>
                    </div>
                    <button 
                        onClick={() => { if (showForm) resetForm(); else setShowForm(true); }} 
                        style={showForm ? btnCancel : btnAdd}
                    >
                        {showForm ? <><X size={18} /> ANNULER</> : <><UserPlus size={18} /> NOUVEL ÉLÈVE</>}
                    </button>
                </header>

                <div style={tabsBarContainer}>
                    <button onClick={() => setActiveTab('liste')} style={activeTab === 'liste' ? activeTabBtn : tabBtn}>
                        <Users size={16} /> Liste des Élèves ({eleves.length})
                    </button>
                    <button onClick={() => setActiveTab('tuteurs')} style={activeTab === 'tuteurs' ? activeTabBtn : tabBtn}>
                        <UserCheck size={16} /> Gestion par Tuteur / Parent ({tuteursListe.length})
                    </button>
                </div>

                {showForm && (
                    <div style={formWrapper}>
                        <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px'}}>
                            <span style={{fontSize: '12px', fontWeight: '900', color: '#2563eb', textTransform: 'uppercase'}}>
                                📝 Inscription d'un nouvel élève & tuteur optionnel
                            </span>
                            <button onClick={resetForm} style={{background: 'none', border: 'none', cursor: 'pointer', color: '#64748b'}}>
                                <X size={16} />
                            </button>
                        </div>
                        <form onSubmit={handleCreateEleve} style={{display: 'flex', flexDirection: 'column', gap: '15px'}}>
                            <div style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '15px'}}>
                                <div style={inputGroup}>
                                    <label style={labelStyle}>NOM & PRÉNOMS DE L'ÉLÈVE *</label>
                                    <input style={inputStyle} required placeholder="Ex: Kouassi Jean" value={eleveForm.nom_eleve} onChange={e => setEleveForm({...eleveForm, nom_eleve: e.target.value})} />
                                </div>
                                <div style={inputGroup}>
                                    <label style={labelStyle}>CLASSE *</label>
                                    <select style={inputStyle} required value={eleveForm.classe_id} onChange={e => setEleveForm({...eleveForm, classe_id: e.target.value})}>
                                        <option value="">Sélectionner la classe...</option>
                                        {classes.map(c => <option key={c.id} value={c.id}>{c.nom_classe}</option>)}
                                    </select>
                                </div>
                                <div style={inputGroup}>
                                    <label style={labelStyle}>ANNÉE SCOLAIRE *</label>
                                    <select style={inputStyle} required value={eleveForm.annee_scolaire_id} onChange={e => setEleveForm({...eleveForm, annee_scolaire_id: e.target.value})}>
                                        <option value="">Sélectionner l'année...</option>
                                        {annees.map(a => <option key={a.id} value={a.id}>{a.libelle}</option>)}
                                    </select>
                                </div>
                                <div style={inputGroup}>
                                    <label style={labelStyle}>TÉLÉPHONE ÉLÈVE</label>
                                    <input style={inputStyle} placeholder="Optionnel" value={eleveForm.telephone_eleve} onChange={e => setEleveForm({...eleveForm, telephone_eleve: e.target.value})} />
                                </div>
                            </div>

                            <hr style={{border: '0', borderTop: '1px solid #e2e8f0', margin: '5px 0'}} />
                            <div style={{fontSize: '11px', fontWeight: '800', color: '#475569', textTransform: 'uppercase'}}>Informations du Tuteur / Parent (Facultatif)</div>

                            <div style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '15px'}}>
                                <div style={inputGroup}>
                                    <label style={labelStyle}>NOM DU TUTEUR</label>
                                    <input style={inputStyle} placeholder="Laisser vide si non fourni" value={eleveForm.nom_tuteur} onChange={e => setEleveForm({...eleveForm, nom_tuteur: e.target.value})} />
                                </div>
                                <div style={inputGroup}>
                                    <label style={labelStyle}>TÉLÉPHONE TUTEUR</label>
                                    <input style={inputStyle} placeholder="Contact parent" value={eleveForm.telephone_tuteur} onChange={e => setEleveForm({...eleveForm, telephone_tuteur: e.target.value})} />
                                </div>
                                <div style={inputGroup}>
                                    <label style={labelStyle}>EMAIL TUTEUR</label>
                                    <input type="email" style={inputStyle} placeholder="Pour accès portail" value={eleveForm.email_tuteur} onChange={e => setEleveForm({...eleveForm, email_tuteur: e.target.value})} />
                                </div>
                                <div style={inputGroup}>
                                    <label style={labelStyle}>LIEN DE PARENTÉ</label>
                                    <input style={inputStyle} placeholder="Ex: Père, Mère, Tuteur" value={eleveForm.lien_parente} onChange={e => setEleveForm({...eleveForm, lien_parente: e.target.value})} />
                                </div>
                            </div>

                            <div style={{display: 'flex', justifyContent: 'flex-end', marginTop: '10px'}}>
                                <button type="submit" disabled={actionLoading} style={btnSubmit}>
                                    ENREGISTRER L'ÉLÈVE
                                </button>
                            </div>
                        </form>
                    </div>
                )}

                <div style={tableWrapper}>
                    {loading ? (
                        <div style={centerStyle}><Users className="animate-spin" size={40} color="#2563eb" /></div>
                    ) : (
                        activeTab === 'liste' ? (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                                {eleves.map(el => {
                                    const ouvert = Boolean(elevesOuverts[el.id]);
                                    return (
                                        <div key={el.id} style={classeCardStyle}>
                                            <div 
                                                onClick={() => toggleEleveAccordion(el.id)}
                                                style={classeHeaderStyle}
                                            >
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer', flex: 1 }}>
                                                    <span style={chevronBox}>{ouvert ? <ChevronDown size={16} /> : <ChevronRight size={16} />}</span>
                                                    <span style={{ fontSize: '13px', fontWeight: '900', color: '#1e293b' }}>👤 {el.nom}</span>
                                                    <span style={badgeCountMatieres}>Classe : {el.nom_classe || 'Non assigné'}</span>
                                                </div>
                                                <span style={{fontSize: '11px', color: '#64748b', fontWeight: '700'}}>Tél: {el.telephone || 'N/A'}</span>
                                            </div>
                                            {ouvert && (
                                                <div style={classeBodyStyle}>
                                                    <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px', fontSize: '12px'}}>
                                                        <div>
                                                            <strong>Informations Élève :</strong>
                                                            <p style={{margin: '4px 0', color: '#475569'}}>Email : {el.email || 'Aucun'}</p>
                                                            <p style={{margin: '4px 0', color: '#475569'}}>Adresse : {el.adresse || 'Aucune'}</p>
                                                        </div>
                                                        <div style={{background: '#f8fafc', padding: '10px', borderRadius: '8px', border: '1px solid #e2e8f0'}}>
                                                            <strong style={{color: '#2563eb'}}>Informations Tuteur Rattaché :</strong>
                                                            {el.tuteur_nom ? (
                                                                <>
                                                                    <p style={{margin: '4px 0'}}>Nom : <strong>{el.tuteur_nom}</strong></p>
                                                                    <p style={{margin: '4px 0'}}>Téléphone : {el.tuteur_telephone || 'N/A'}</p>
                                                                    <p style={{margin: '4px 0'}}>Lien : {el.lien_parente || 'Parent'}</p>
                                                                </>
                                                            ) : (
                                                                <p style={{margin: '4px 0', color: '#94a3b8', fontStyle: 'italic'}}>Aucun tuteur rattaché pour le moment.</p>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                                {eleves.length === 0 && <div style={emptyStyle}>Aucun élève enregistré.</div>}
                            </div>
                        ) : (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                                {tuteursListe.map(tut => {
                                    const ouvert = Boolean(tuteursOuverts[tut.id]);
                                    const enfants = tut.enfants || [];
                                    return (
                                        <div key={tut.id} style={classeCardStyle}>
                                            <div onClick={() => toggleTuteurAccordion(tut.id)} style={classeHeaderStyle}>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer', flex: 1 }}>
                                                    <span style={chevronBox}>{ouvert ? <ChevronDown size={16} /> : <ChevronRight size={16} />}</span>
                                                    <span style={{ fontSize: '13px', fontWeight: '900', color: '#1e293b' }}>👪 Tuteur : {tut.nom}</span>
                                                    <span style={badgeCountMatieres}>{enfants.length} enfant(s) rattaché(s)</span>
                                                </div>
                                                <span style={{fontSize: '11px', color: '#64748b', fontWeight: '700'}}>Tél: {tut.telephone || 'N/A'}</span>
                                            </div>
                                            {ouvert && (
                                                <div style={classeBodyStyle}>
                                                    <strong style={{fontSize: '11px', textTransform: 'uppercase', color: '#64748b'}}>Liste des enfants de ce tuteur :</strong>
                                                    <div style={{display: 'flex', flexDirection: 'column', gap: '8px', marginTop: '8px'}}>
                                                        {enfants.length > 0 ? enfants.map(enf => (
                                                            <div key={enf.id} style={{background: '#f8fafc', padding: '8px 12px', borderRadius: '6px', border: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', fontSize: '12px'}}>
                                                                <span>🎓 <strong>{enf.nom}</strong> (Classe: {enf.nom_classe || 'N/A'})</span>
                                                                <span style={{color: '#64748b'}}>Lien : {enf.lien_parente || 'Parent'}</span>
                                                            </div>
                                                        )) : <p style={{color: '#94a3b8', fontSize: '12px', fontStyle: 'italic'}}>Aucun enfant rattaché.</p>}
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                                {tuteursListe.length === 0 && <div style={emptyStyle}>Aucun tuteur trouvé.</div>}
                            </div>
                        )
                    )}
                </div>

                {toast.show && (
                    <div style={{...toastStyle, background: toast.type === 'success' ? '#0f172a' : '#dc2626'}}>
                        {toast.type === 'success' ? <CheckCircle2 size={18} color="#10b981" /> : <AlertTriangle size={18} color="white" />}
                        {toast.message}
                    </div>
                )}
            </main>
        </div>
    );
};

// Styles (réutilisant la charte graphique globale)
const layoutStyle = { display: 'flex', height: '100vh', background: '#f8fafc', overflow: 'hidden' };
const mainStyle = { flex: 1, display: 'flex', flexDirection: 'column' };
const headerStyle = { background: 'white', padding: '20px 40px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #e2e8f0' };
const titleContainer = { display: 'flex', alignItems: 'center', gap: '15px' };
const titleStyle = { margin: 0, fontSize: '18px', fontWeight: '900', color: '#0f172a' };
const subtitleStyle = { fontSize: '12px', color: '#64748b', fontWeight: '700', display: 'flex', alignItems: 'center', gap: '5px' };
const iconBoxStyle = { background: '#2563eb', padding: '10px', borderRadius: '12px' };
const tabsBarContainer = { display: 'flex', alignItems: 'center', background: 'white', padding: '10px 40px', borderBottom: '1px solid #e2e8f0', gap: '10px' };
const tabBtn = { display: 'flex', alignItems: 'center', gap: '8px', background: '#f1f5f9', border: 'none', padding: '10px 20px', borderRadius: '10px', fontSize: '12px', fontWeight: '800', color: '#64748b', cursor: 'pointer' };
const activeTabBtn = { ...tabBtn, background: '#0f172a', color: 'white' };
const tableWrapper = { padding: '20px 40px', flex: 1, overflowY: 'auto' };
const classeCardStyle = { background: 'white', borderRadius: '12px', border: '1px solid #e2e8f0', overflow: 'hidden', boxShadow: '0 2px 4px rgba(0,0,0,0.02)' };
const classeHeaderStyle = { background: '#f8fafc', padding: '15px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #e2e8f0' };
const chevronBox = { background: '#e2e8f0', padding: '4px', borderRadius: '6px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#334155' };
const badgeCountMatieres = { background: '#f1f5f9', color: '#475569', padding: '3px 8px', borderRadius: '6px', fontSize: '11px', fontWeight: '700' };
const classeBodyStyle = { padding: '15px 20px' };
const emptyStyle = { textAlign: 'center', padding: '40px', color: '#64748b', fontWeight: '600' };
const formWrapper = { background: 'white', padding: '20px 40px', borderBottom: '1px solid #e2e8f0' };
const inputGroup = { display: 'flex', flexDirection: 'column', gap: '6px', flex: 1, minWidth: '200px' };
const labelStyle = { fontSize: '10px', fontWeight: '900', color: '#64748b' };
const inputStyle = { padding: '8px 10px', borderRadius: '8px', border: '1px solid #cbd5e1', fontWeight: '700', fontSize: '12px', width: '100%', background: 'white' };
const btnSubmit = { background: '#059669', color: 'white', border: 'none', padding: '9px 20px', borderRadius: '8px', fontWeight: '800', cursor: 'pointer', height: '38px', fontSize: '11px' };
const btnAdd = { background: '#0f172a', color: 'white', border: 'none', padding: '10px 20px', borderRadius: '10px', fontWeight: '800', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px' };
const btnCancel = { ...btnAdd, background: '#fee2e2', color: '#dc2626' };
const toastStyle = { position: 'fixed', top: '20px', right: '20px', padding: '15px 30px', borderRadius: '12px', color: 'white', display: 'flex', alignItems: 'center', gap: '12px', zIndex: 9999, fontWeight: '700' };
const centerStyle = { display: 'flex', justifyContent: 'center', padding: '100px' };

export default Eleves;